// THE SWITCH for omnisearch.
//
// The search boxes (`components/header/Header.tsx` and
// `components/stock-search-bar/Searchbar.tsx`) call `searchInstruments`, which asks
// this module whether to use the new `/interface/api/search/` endpoint or the legacy
// `/API/v2/find/`. With the switch off, `searchInstruments` is exactly
// `findInstruments` - same request, same response, same UI, same 500 ms wait.
// With it on, the boxes search on the keystroke, with no wait.
//
// Turned ON for everyone on 2026-09-12. To turn it off:
//
//  1. **One browser, no deploy:** open any page with `?omnisearch=0`. The choice is
//     remembered in localStorage until `?omnisearch=1`. Also the way to try it on or
//     off for yourself without affecting anyone else.
//  2. **Everyone, no code change:** set `NEXT_PUBLIC_OMNISEARCH=0` in the Vercel project
//     and redeploy.
//  3. **Everyone, in code:** change `OMNISEARCH_DEFAULT` below to `false`.
//
// A per-browser choice (1) always wins over (2) and (3).
//
// Only the search boxes are switched. `findInstruments` has other callers - the charts
// datafeed, the options analyzer, the SI badge cache, the tickertape - that use it to
// *resolve a ticker*, not to search. Changing how those resolve is a different decision
// with a much larger blast radius, so they keep calling `findInstruments` directly.

export const OMNISEARCH_DEFAULT = true;

const PARAM = "omnisearch";
const STORAGE_KEY = "ortex.omnisearch";

/** Keystroke wait before searching. The legacy value existed to protect a slow backend. */
const LEGACY_DELAY_MS = 500;

function fromEnv(): boolean | null {
    const value = process.env.NEXT_PUBLIC_OMNISEARCH;
    if (value === "1" || value === "true") return true;
    if (value === "0" || value === "false") return false;
    return null;
}

/** Whether the search boxes should use the new omnisearch endpoint. */
export function isOmnisearchEnabled(): boolean {
    const fallback = fromEnv() ?? OMNISEARCH_DEFAULT;
    if (typeof window === "undefined") return fallback;
    try {
        const param = new URLSearchParams(window.location.search).get(PARAM);
        if (param === "1" || param === "0") {
            window.localStorage.setItem(STORAGE_KEY, param);
            return param === "1";
        }
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === "1" || stored === "0") return stored === "1";
    } catch {
        // Storage can throw (private mode, blocked site data). The build default stands.
    }
    return fallback;
}

/**
 * How long a search box waits after a keystroke before searching: only with the switch
 * off. With omnisearch on there is no wait at all; the boxes send on the keystroke through
 * `LatestSearch` (`client.ts`), which coalesces instead (ortex-gui#1197).
 */
export function searchDelayMs(): number {
    return isOmnisearchEnabled() ? 0 : LEGACY_DELAY_MS;
}
